import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Card, CardContent } from "@/components/ui/card";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Privacy Policy"
        description="Read RunoBlog's privacy policy to understand how we collect, use, and protect your personal information across our website and services."
        path="/privacy"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="border-b border-border bg-gradient-to-br from-primary/5 to-accent/5">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl">
              Privacy Policy
            </h1>
            <p className="text-lg text-muted-foreground">
              Last updated: November 11, 2025
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Content */}
      <section className="container mx-auto px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <Card>
            <CardContent className="prose prose-slate max-w-none pt-6 dark:prose-invert">
              <div className="space-y-8">
                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">1. Introduction</h2>
                  <p className="text-muted-foreground">
                    Welcome to RunoBlog. We respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and protect your information when you visit our website, and outlines your rights under applicable privacy laws such as the GDPR, CCPA, and other global data protection regulations.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">2. Information We Collect</h2>
                  <p className="text-muted-foreground">
                    We may collect, use, store, and transfer different kinds of personal data about you, including:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li><strong>Identity Data:</strong> First name, last name, username or similar identifier.</li>
                    <li><strong>Contact Data:</strong> Email address and telephone number.</li>
                    <li><strong>Technical Data:</strong> Internet protocol (IP) address, browser type and version, time zone setting and location, operating system and platform.</li>
                    <li><strong>Usage Data:</strong> Information about how you use our website, products, and services.</li>
                    <li><strong>Marketing and Communications Data:</strong> Your preferences in receiving marketing from us and your communication preferences.</li>
                  </ul>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">3. How We Use Your Information</h2>
                  <p className="text-muted-foreground">
                    We use your personal data in accordance with applicable laws for purposes such as:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>To provide and maintain our services</li>
                    <li>To notify you about changes to our website or services</li>
                    <li>To provide customer support</li>
                    <li>To gather analytics and improve user experience</li>
                    <li>To monitor website usage and detect/prevent technical issues</li>
                    <li>To send you newsletters or marketing content (only with your consent)</li>
                  </ul>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">4. Cookies and Tracking Technologies</h2>
                  <p className="text-muted-foreground">
                    We use cookies and similar technologies to track activity on our website and store certain information. Cookies help us analyze website traffic, personalize content, and deliver relevant advertisements.
                  </p>
                  <p className="mt-4 text-muted-foreground">
                    You can manage or withdraw your cookie consent anytime by:
                  </p>
                  <ul className="mt-2 space-y-2 text-muted-foreground">
                    <li>Adjusting your browser settings to refuse cookies, or</li>
                    <li>Using our website's cookie consent tool (if available).</li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    For more information about cookies, visit <a href="https://www.allaboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.allaboutcookies.org</a>.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">5. Data Security</h2>
                  <p className="text-muted-foreground">
                    We have implemented appropriate technical and organizational security measures to protect your personal data from unauthorized access, alteration, disclosure, or destruction. Access to your data is limited to employees and partners who have a legitimate business need to process it.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">6. Data Retention</h2>
                  <p className="text-muted-foreground">
                    We retain your personal data only as long as necessary to fulfill the purposes for which it was collected, including legal, accounting, or reporting requirements.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">7. Your Legal Rights</h2>
                  <p className="text-muted-foreground">
                    Depending on your location, you may have the right to:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>Request access to your personal data</li>
                    <li>Request correction or deletion of your data</li>
                    <li>Object to or restrict certain types of processing</li>
                    <li>Request data portability</li>
                    <li>Withdraw consent at any time</li>
                  </ul>
                  <p className="mt-4 text-muted-foreground">
                    To exercise any of these rights, please contact us using the details below.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">8. Third-Party Links</h2>
                  <p className="text-muted-foreground">
                    Our website may include links to third-party websites, plugins, and applications. Clicking on those links may allow third parties to collect or share data about you. We are not responsible for the privacy practices of those sites.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">9. Third-Party Advertising Partners</h2>
                  <p className="text-muted-foreground">
                    We use third-party advertising partners, including Google, Adsterra, and their affiliates, to display ads on this website. These partners may use cookies, web beacons, popunder ads, or other technologies to collect information (such as IP address, browsing behavior, or inferred interests) to provide personalized ads.
                  </p>
                  <p className="mt-4 text-muted-foreground">
                    You can learn more about how Google uses information from partner sites here:{" "}
                    <a href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      https://policies.google.com/technologies/partner-sites
                    </a>
                  </p>
                  <p className="mt-4 text-muted-foreground">
                    You can opt out of personalized advertising through the AdChoices program:{" "}
                    <a href="https://www.aboutads.info/choices/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      https://www.aboutads.info/choices/
                    </a>
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">10. Changes to This Privacy Policy</h2>
                  <p className="text-muted-foreground">
                    We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date.
                  </p>
                </div>

                <div>
                  <h2 className="mb-4 text-2xl font-bold text-foreground">11. Contact Us</h2>
                  <p className="text-muted-foreground">
                    If you have any questions about this Privacy Policy, please contact us at:
                  </p>
                  <ul className="mt-4 space-y-2 text-muted-foreground">
                    <li>📧 Email: runoux.dev@gmail.com</li>
                    <li>🌐 Website: <a href="https://www.runoblog.site" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">https://www.runoblog.site</a></li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Privacy;
